import { tokenStore } from "./token";

const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

interface ApiOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

async function doFetch(path: string, opts: ApiOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  const token = tokenStore.get();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  return fetch(`${API_BASE}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: "same-origin",
  });
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  // Proactive refresh — если до конца жизни токена меньше LEEWAY, обновим
  // заранее, чтобы не словить 401 в полёте. Параллельные вызовы
  // дедуплицируются внутри tokenStore.
  await tokenStore.ensureFresh().catch(() => false);

  let res = await doFetch(path, opts);

  // Если access всё-таки умер между ensureFresh и fetch, или сервер посчитал
  // токен невалидным по другой причине — одна попытка refresh + retry.
  if (res.status === 401 && tokenStore.getRefresh()) {
    const ok = await tokenStore.refreshNow().catch(() => false);
    if (ok) {
      res = await doFetch(path, opts);
    }
  }

  if (res.status === 401) {
    tokenStore.clear();
    if (!window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
    throw new ApiError(401, "unauthenticated", "Session expired");
  }

  const ct = res.headers.get("content-type") ?? "";
  const payload = ct.includes("application/json") ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    throw new ApiError(
      res.status,
      payload?.error ?? "http_error",
      payload?.message ?? `HTTP ${res.status}`,
      payload?.details,
    );
  }

  return payload as T;
}
